import { FullScreenLoader } from '@slims/ui/atoms';
import { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { subscribeToTable } from '@/lib/supabase/realtime-registry';
import { useAuthStore } from '@/stores/auth-store';

/**
 * Client-side auth guard (replaces the Next.js middleware). Redirects to
 * /sign-in when there's no Supabase session, otherwise loads the auth context.
 *
 * La sessione non viene controllata solo al mount: quando il token scade e il
 * refresh fallisce, supabase-js emette `SIGNED_OUT` con sessione nulla. Senza
 * ascoltarlo l'utente resterebbe su una pagina che sembra viva ma riceve 401 da
 * ogni chiamata, invece di tornare al login (INB-6, sessioni con scadenza).
 */
export function ProtectedRoute() {
	const location = useLocation();
	const [checking, setChecking] = useState(true);
	const [authed, setAuthed] = useState(false);
	const fetchAuth = useAuthStore((s) => s.fetchAuth);
	const clear = useAuthStore((s) => s.clear);
	const loaded = useAuthStore((s) => s.loaded);
	const userId = useAuthStore((s) => s.userId);
	const role = useAuthStore((s) => s.role);

	// A superadmin changing this user's role/membership from another session
	// (superadmin-user-detail) must not leave this tab on stale RBAC — role and
	// availableOrgs otherwise stick at whatever /auth/me returned on first load.
	useEffect(() => {
		if (!userId) return;
		return subscribeToTable(
			`user-org-memberships-${userId}`,
			{
				event: '*',
				schema: 'public',
				table: 'user_org_memberships',
				filter: `user_id=eq.${userId}`,
			},
			() => {
				fetchAuth();
			},
		);
	}, [userId, fetchAuth]);

	useEffect(() => {
		let active = true;
		supabase.auth.getSession().then(({ data: { session } }) => {
			if (!active) return;
			setAuthed(!!session);
			setChecking(false);
			if (session) fetchAuth();
		});

		const {
			data: { subscription },
		} = supabase.auth.onAuthStateChange((_event, session) => {
			if (!active || session) return;
			clear();
			setAuthed(false);
			setChecking(false);
		});

		return () => {
			active = false;
			subscription.unsubscribe();
		};
	}, [fetchAuth, clear]);

	if (checking || (authed && !loaded)) {
		return <FullScreenLoader />;
	}
	if (!authed) return <Navigate to="/sign-in" replace state={{ from: location.pathname }} />;
	// Gate before mounting page data hooks, including bookmarks to admin pages.
	if (
		role === 'member' &&
		!/^\/(myslims|dashboard(?:\/consumption|\/ai-forecast)?|monitoring|forecast)\/?$/i.test(
			location.pathname,
		)
	) {
		return <Navigate to="/myslims" replace />;
	}
	return <Outlet />;
}
